import type { SidebarLiquidGlassConfig } from './Sidebar.types'

/**
 * Valores por defecto para Liquid Glass V2
 *
 * @description
 * Configuración conservadora que prioriza compatibilidad y performance:
 * - Liquid Glass V2 desactivado por defecto (opt-in)
 * - Chromatic Aberration desactivado (requiere opt-in explícito)
 * - Edge Glass 3D y Backdrop Extension activados cuando V2 está habilitado
 *
 * @example
 * ```tsx
 * // Activar todas las features V2
 * <Sidebar
 *   liquidGlass={{
 *     enableLiquidGlassV2: true,
 *     enableChromaticAberration: true
 *   }}
 * />
 * ```
 */
export const DEFAULT_LIQUID_GLASS_CONFIG: SidebarLiquidGlassConfig = {
  /**
   * V2 desactivado por defecto (backward compatibility)
   *
   * Cuando está desactivado, usa FluidHoverIndicator y SidebarTooltip originales
   * con backdrop-blur CSS básico.
   */
  enableLiquidGlassV2: false,

  /**
   * Chromatic aberration desactivado por defecto
   *
   * Requiere activación explícita debido a:
   * - Costo computacional adicional (filtros SVG complejos)
   * - Efecto visual intenso que puede no ser adecuado para todos los temas
   * - Requiere que enableLiquidGlassV2 = true
   */
  enableChromaticAberration: false,

  /**
   * Edge Glass 3D activado por defecto (cuando V2 está habilitado)
   *
   * Proporciona profundidad visual significativa con costo computacional aceptable.
   * Solo aplica a SidebarTooltipV2.
   */
  enableEdgeGlass3D: true,

  /**
   * Backdrop Extension activado por defecto (cuando V2 está habilitado)
   *
   * Técnica de Josh Comeau para mejorar realismo de refracción.
   * Captura elementos nearby con 200% height + mask.
   * Solo aplica a SidebarTooltipV2.
   */
  enableBackdropExtension: true,

  /**
   * Animaciones de items activadas por defecto
   *
   * Staggered animations para entrada/salida de items del tooltip.
   * Incluye grid trick para height auto transitions.
   */
  enableTooltipItemAnimations: true,

  /**
   * Animación 3D de cilindro para títulos desactivada por defecto
   *
   * Efecto experimental de rotación 3D para títulos del tooltip.
   * Requiere opt-in explícito.
   */
  enableCylinderTitle: false
}

/**
 * Helper para merge de configuración liquid glass con defaults
 *
 * @example
 * ```tsx
 * const config = mergeLiquidGlassConfig({ enableLiquidGlassV2: true })
 * // { enableLiquidGlassV2: true, enableChromaticAberration: false, ... }
 * ```
 */
export function mergeLiquidGlassConfig(
  override?: Partial<SidebarLiquidGlassConfig>
): SidebarLiquidGlassConfig {
  return {
    ...DEFAULT_LIQUID_GLASS_CONFIG,
    ...override
  }
}

/**
 * Validar configuración de liquid glass
 *
 * @description
 * Verifica que las flags dependientes tengan sus requisitos satisfechos:
 * - Chromatic Aberration requiere Liquid Glass V2
 * - Edge Glass 3D requiere Liquid Glass V2
 * - Backdrop Extension requiere Liquid Glass V2
 *
 * @returns Configuración validada con warnings en consola si hay conflictos
 */
export function validateLiquidGlassConfig(
  config: SidebarLiquidGlassConfig
): SidebarLiquidGlassConfig {
  const validated = { ...config }

  // Si V2 está desactivado, deshabilitar todas las features dependientes
  if (!config.enableLiquidGlassV2) {
    if (config.enableChromaticAberration) {
      console.warn(
        '[Sidebar] enableChromaticAberration requiere enableLiquidGlassV2 = true. ' +
        'Deshabilitando chromatic aberration.'
      )
      validated.enableChromaticAberration = false
    }

    if (config.enableEdgeGlass3D) {
      console.warn(
        '[Sidebar] enableEdgeGlass3D requiere enableLiquidGlassV2 = true. ' +
        'Deshabilitando edge glass 3D.'
      )
      validated.enableEdgeGlass3D = false
    }

    if (config.enableBackdropExtension) {
      console.warn(
        '[Sidebar] enableBackdropExtension requiere enableLiquidGlassV2 = true. ' +
        'Deshabilitando backdrop extension.'
      )
      validated.enableBackdropExtension = false
    }
  }

  return validated
}

/**
 * Tipo guard para verificar si Liquid Glass V2 está habilitado
 */
export function isLiquidGlassV2Enabled(
  config: SidebarLiquidGlassConfig
): boolean {
  return config.enableLiquidGlassV2 === true
}

/**
 * Tipo guard para verificar si Chromatic Aberration está habilitado
 * (y sus requisitos están satisfechos)
 */
export function isChromaticAberrationEnabled(
  config: SidebarLiquidGlassConfig
): boolean {
  return config.enableLiquidGlassV2 === true &&
         config.enableChromaticAberration === true
}
